import { useDagschoothoogteHeight } from "app/hooks/configurator/useDagschoothoogteHeight";
import { useOpdekdeurHingeHeight } from "app/hooks/configurator/useOpdekdeurHingeHeight";
import { useConfigurationOptionsContext } from "app/providers/DoorOptionsProvider";
import { EigenwijsOptions } from "app/providers/DoorOptionsProvider.types";
import MeasurementOptionControls from "features/option-controls/measurements-option-controls";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SelfAssemblyMeasurements } from "shared/api/middleware-api-client";
import FormGroup from "shared/ui/FormGroup";
import RightOrientation from "shared/ui/icons/selfAssembly/RightOrientation";
import Input from "shared/ui/input";
import Label from "shared/ui/label";
import { Delimiter } from "widgets/business-configurator/door-options/Delimiter";
import ToggleButton from "widgets/shared/toggle/components/ToggleButton";
import { ToggleLayout } from "widgets/shared/toggle/components/ToggleLayout";
import { AansluitingKozijnOptions } from "./AansluitingKozijnOptions";

interface Props {
  onShowInfoClick: ({
    title,
    description,
  }: {
    title: string;
    description: string;
  }) => void;
}
const KaderdeurSelfInstallment = ({ onShowInfoClick }: Props) => {
  const [searchParams] = useSearchParams();

  const {
    eigenwijs,
    handle,
    updateSelfAssembleData,
    updateSearchParam,
    selfAssembleDoorData,
    updateAnchorBeams,
    updateEigenwijs,
  } = useConfigurationOptionsContext();
  const { getDagschoothoogteHeight } = useDagschoothoogteHeight();

  const [isDefaultDataInitialized, setIsDefaultDataInitialized] =
    useState(false);

  const doorMeasurements = useMemo(() => {
    let width = 0;
    let height = 0;

    if (eigenwijs) {
      width = eigenwijs.sizeObjects[0].customWidth;
      height = eigenwijs.sizeObjects[0].customHeight;
    }

    return { width, height };
  }, [eigenwijs?.sizeObjects]);

  useEffect(() => {
    initializeDefaultKaderdeurSelfInstallmentData();
  }, []);
  useEffect(() => {
    if (!isDefaultDataInitialized) return;
    setDoorMeasurements(doorMeasurements);
  }, [isDefaultDataInitialized, doorMeasurements]);

  const initializeDefaultKaderdeurSelfInstallmentData = () => {
    const data = { ...selfAssembleDoorData };

    data.measurements = {
      rotation: "left", // Draairichting left|right
      easyToReach: false, //Default
      deliverInParts: false, //Default
      onLocation: false, //Default
      extraFloor: true, //Default
      scheluwHangTop: 0, //Default
      scheluwHangBottom: 0, //Default
      scheluwSluitTop: 0, //Default
      scheluwSluitBottom: 0, //Default
      dagschoothoogte: getDagschoothoogteHeight(doorMeasurements.height), //Default,
      skews: {
        width: [],
        height: [],
        openHeight: [],
      },
      frameConnection: searchParams.get("frameConnection") ?? "op-enkel-glas",
    };

    if (searchParams.get("label")) data.label = searchParams.get("label") || "";
    if (searchParams.get("rotation"))
      data.measurements.rotation = searchParams.get("rotation") || "";

    updateSelfAssembleData(data);
    setIsDefaultDataInitialized(true);
  };

  const setDoorMeasurements = (doorMeasurements: {
    width: number;
    height: number;
  }) => {
    const data = { ...selfAssembleDoorData };
    data.measurements.dagschoothoogte = getDagschoothoogteHeight(
      doorMeasurements.height,
    );
    data.measurements.sponningsBreedte = doorMeasurements.width;
    data.measurements.sponningsHoogte = doorMeasurements.height;
    data.measurements.skews = {
      width: Array(4)
        .fill(0)
        .map(() => ({
          size: doorMeasurements.width,
          offset: 0,
        })),
      height: Array(3)
        .fill(0)
        .map(() => ({
          size: doorMeasurements.height,
          offset: 0,
        })),
      openHeight: [
        [],
        [],
        [],
        [
          {
            offset: 0, //Default
          },
          {
            offset: 0, //Default
          },
        ],
      ],
    };

    updateSearchParam("sponningsBreedte", String(doorMeasurements.width));
    updateSearchParam("sponningsHoogte", String(doorMeasurements.height));
    updateSelfAssembleData(data);
  };

  const setRotationDirection = (direction: string) => {
    let data = { ...selfAssembleDoorData };
    data.measurements.rotation = direction;

    updateSearchParam("rotation", direction);
    updateSelfAssembleData(data);
  };

  // Determine if the rotation direction is left
  const isRotationDirectionLeft =
    selfAssembleDoorData.measurements.rotation === "left";

  return (
    <div className="gg-flex gg-flex-col gg-gap-y-5">
      <FormGroup className="gg-mb-1">
        <Label
          htmlFor="typeDeur"
          className="gg-mb-2 gg-mt-5 gg-text-sm gg-font-bold"
        >
          Aansluiting kozijn
        </Label>

        <AansluitingKozijnOptions />
      </FormGroup>
      <FormGroup className="gg-mb-1">
        <Label
          htmlFor="rotation-direction"
          className="gg-mb-2 gg-text-sm gg-font-bold"
          onInfoButtonClick={() =>
            onShowInfoClick({
              title: "Draairichting",
              description:
                "Om de draairichting te bepalen, ga je in de ruimte staan waar je de deur naar je toe moet trekken om 'm te openen. Een deur met scharnieren aan de linkerzijde is linksdraaiend. Een deur met scharnieren aan de rechterzijde is rechtsdraaiend.",
            })
          }
          showInfoButton
        >
          Draairichting
        </Label>

        <ToggleLayout>
          <ToggleButton
            isActive={isRotationDirectionLeft}
            onClick={() => setRotationDirection("left")}
            roundedClass="gg-rounded-l-sm gg-border-l gg-grid gg-place-items-center "
            layoutId="rotationDirectionToggle"
            dataTestId="rotationDirectionToggleLeft"
          >
            <RightOrientation
              className="gg-mb-2 gg-scale-x-[-1] gg-pt-2 sm:gg-mb-4"
              isActive={isRotationDirectionLeft}
            />
            Linksdraaiend
          </ToggleButton>

          <ToggleButton
            isActive={!isRotationDirectionLeft}
            onClick={() => setRotationDirection("right")}
            roundedClass="gg-rounded-r-sm gg-border-r gg-grid gg-place-items-center "
            layoutId="rotationDirectionToggle"
            dataTestId="rotationDirectionToggleRight"
          >
            <RightOrientation
              className="gg-mb-2 gg-pt-2 sm:gg-mb-4"
              isActive={!isRotationDirectionLeft}
            />
            Rechtsdraaiend
          </ToggleButton>
        </ToggleLayout>
      </FormGroup>

      <MeasurementOptionControls />
    </div>
  );
};
export default KaderdeurSelfInstallment;
